import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';
import '../styles/Commissioning.css';

const EVAC_TASKS = [
  { key: 'evac1', label: 'Evacuate the system to 10 Torr from both service valves' },
  { key: 'evac2', label: 'Break to vacuum with OFN (N₂) into the suction service valve to 1 bar' },
  { key: 'evac3', label: 'Evacuate to 5 Torr from the discharge valve' },
  { key: 'evac4', label: 'Break to vacuum with OFN (N₂) into the suction service valve to 1 bar' },
  { key: 'evac5', label: 'Evacuate to lowest pressure vacuum pump will achieve (2 Torr for 1 hour minimum)' },
  { key: 'evac6', label: 'Rise Test must then be carried out for a minimum of 30 minutes' },
];

const YES_NO = ['Y', 'N', 'N/A'];

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatDateUK = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
};

const numOrZero = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const initialForm = {
  contractNo: '',
  date: todayISO(),
  siteLocation: '',
  engineer: '',
  unitNo: '',
  refrigerant: '',
  pressureTest: 'Y',
  modelInd: '',
  modelOut: '',
  serialInd: '',
  serialOut: '',
  evac1: 'Y',
  evac2: 'Y',
  evac3: 'Y',
  evac4: 'Y',
  evac5: 'Y',
  evac6: 'Y',
  totalPipeLength: '',
  factoryPipeRun: '',
  additionalPerMetre: '',
  factoryCharge: '',
  check1: 'Y',
  check2: 'Y',
  check3: '',
  check3Detail: '',
  check4: '',
  check5: '',
  suctionSize: '',
  liquidSize: '',
  check7: 'Y',
  check8: 'OK',
  check9: 'Y',
  check10: 'Y',
  check11: 'Y',
  coolL1: '',
  coolL2: '',
  coolL3: '',
  heatL1: '',
  heatL2: '',
  heatL3: '',
  coolAirOn: '',
  coolAirOff: '',
  heatAirOn: '',
  heatAirOff: '',
  notes: '',
  witnessedBy: '',
  witnessDate: todayISO(),
};

function Commissioning() {
  const [form, setForm] = useState(initialForm);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const pipeDifference = useMemo(() => {
    const diff = numOrZero(form.totalPipeLength) - numOrZero(form.factoryPipeRun);
    return Number(diff.toFixed(2));
  }, [form.totalPipeLength, form.factoryPipeRun]);

  const additionalChargeKg = useMemo(() => {
    const extra = Math.max(pipeDifference, 0) * numOrZero(form.additionalPerMetre);
    return Number(extra.toFixed(3));
  }, [pipeDifference, form.additionalPerMetre]);

  const totalCharge = useMemo(() => {
    return Number((numOrZero(form.factoryCharge) + additionalChargeKg).toFixed(3));
  }, [form.factoryCharge, additionalChargeKg]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError('');
    setSuccess('');
  };

  const validate = () => {
    const required = [
      ['contractNo', 'Contract No'],
      ['siteLocation', 'Site Location'],
      ['engineer', 'Engineer'],
      ['unitNo', 'Unit No'],
      ['refrigerant', 'Refrigerant'],
    ];
    for (const [key, label] of required) {
      if (!String(form[key] || '').trim()) {
        return `${label} is required`;
      }
    }
    return '';
  };

  const buildTemplateData = () => ({
    contractNo: form.contractNo,
    date: formatDateUK(form.date),
    siteLocation: form.siteLocation,
    engineer: form.engineer,
    unitNo: form.unitNo,
    refrigerant: form.refrigerant,
    pressureTest: form.pressureTest,
    modelInd: form.modelInd,
    modelOut: form.modelOut,
    serialInd: form.serialInd,
    serialOut: form.serialOut,
    evac1: form.evac1,
    evac2: form.evac2,
    evac3: form.evac3,
    evac4: form.evac4,
    evac5: form.evac5,
    evac6: form.evac6,
    totalPipeLength: form.totalPipeLength === '' ? '' : String(numOrZero(form.totalPipeLength)),
    factoryPipeRun: form.factoryPipeRun === '' ? '' : String(numOrZero(form.factoryPipeRun)),
    pipeDifference: String(pipeDifference),
    additionalPerMetre: form.additionalPerMetre === '' ? '' : String(numOrZero(form.additionalPerMetre)),
    factoryCharge: form.factoryCharge === '' ? '' : String(numOrZero(form.factoryCharge)),
    totalCharge: String(totalCharge),
    check1: form.check1,
    check2: form.check2,
    check3: form.check3,
    check3Detail: form.check3Detail,
    check4: form.check4,
    check5: form.check5,
    suctionSize: form.suctionSize,
    liquidSize: form.liquidSize,
    check7: form.check7,
    check8: form.check8,
    check9: form.check9,
    check10: form.check10,
    check11: form.check11,
    coolL1: form.coolL1,
    coolL2: form.coolL2,
    coolL3: form.coolL3,
    heatL1: form.heatL1,
    heatL2: form.heatL2,
    heatL3: form.heatL3,
    coolAirOn: form.coolAirOn,
    coolAirOff: form.coolAirOff,
    heatAirOn: form.heatAirOn,
    heatAirOff: form.heatAirOff,
    notes: form.notes || '',
    witnessedBy: form.witnessedBy,
    witnessDate: formatDateUK(form.witnessDate),
  });

  const generateSheet = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setGenerating(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/templates/CommissioningSheet.docx`);
      if (!response.ok) {
        throw new Error('Could not load commissioning template');
      }

      const arrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        delimiters: { start: '{', end: '}' },
      });

      doc.render(buildTemplateData());

      const output = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      const safeSite = (form.siteLocation || 'site').replace(/[^\w-]+/g, '_').slice(0, 40);
      const safeUnit = (form.unitNo || 'unit').replace(/[^\w-]+/g, '_').slice(0, 20);
      const fileName = `Commissioning_${safeSite}_${safeUnit}.docx`;

      saveAs(output, fileName);
      setSuccess(`Downloaded ${fileName}`);
    } catch (err) {
      console.error('Commissioning sheet generation failed:', err);
      setError(err.message || 'Failed to generate commissioning sheet');
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setError('');
    setSuccess('');
  };

  return (
    <div className="commissioning-container">
      <div className="commissioning-header">
        <div className="commissioning-header-left">
          <Link to="/dashboard" className="home-button">
            ← Home
          </Link>
          <h1 className="commissioning-title">Commissioning</h1>
        </div>
      </div>

      <form className="commissioning-form" onSubmit={generateSheet}>
        <section className="commissioning-section">
          <h2>Job Details</h2>
          <div className="form-grid">
            <label>
              Contract No *
              <input value={form.contractNo} onChange={(e) => updateField('contractNo', e.target.value)} />
            </label>
            <label>
              Date *
              <input type="date" value={form.date} onChange={(e) => updateField('date', e.target.value)} />
            </label>
            <label className="full-width">
              Site Location *
              <input value={form.siteLocation} onChange={(e) => updateField('siteLocation', e.target.value)} />
            </label>
            <label>
              Engineer *
              <input value={form.engineer} onChange={(e) => updateField('engineer', e.target.value)} />
            </label>
            <label>
              Unit No *
              <input value={form.unitNo} onChange={(e) => updateField('unitNo', e.target.value)} />
            </label>
            <label>
              Refrigerant *
              <input value={form.refrigerant} onChange={(e) => updateField('refrigerant', e.target.value)} placeholder="e.g. R32" />
            </label>
            <label>
              Pressure Test
              <select value={form.pressureTest} onChange={(e) => updateField('pressureTest', e.target.value)}>
                {YES_NO.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <label>
              Model No (Indoor)
              <input value={form.modelInd} onChange={(e) => updateField('modelInd', e.target.value)} />
            </label>
            <label>
              Model No (Outdoor)
              <input value={form.modelOut} onChange={(e) => updateField('modelOut', e.target.value)} />
            </label>
            <label>
              Serial No (Indoor)
              <input value={form.serialInd} onChange={(e) => updateField('serialInd', e.target.value)} />
            </label>
            <label>
              Serial No (Outdoor)
              <input value={form.serialOut} onChange={(e) => updateField('serialOut', e.target.value)} />
            </label>
          </div>
        </section>

        <section className="commissioning-section">
          <h2>Evacuation</h2>
          <div className="checklist">
            {EVAC_TASKS.map((task, index) => (
              <div key={task.key} className="checklist-row">
                <span className="checklist-index">{index + 1}</span>
                <span className="checklist-label">{task.label}</span>
                <select
                  value={form[task.key]}
                  onChange={(e) => updateField(task.key, e.target.value)}
                >
                  {YES_NO.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </section>

        <section className="commissioning-section">
          <h2>Refrigerant Charge</h2>
          <div className="form-grid">
            <label>
              Total Pipe Length (m)
              <input
                type="number"
                step="0.01"
                value={form.totalPipeLength}
                onChange={(e) => updateField('totalPipeLength', e.target.value)}
              />
            </label>
            <label>
              Factory Charge Allowable Pipe Run (m)
              <input
                type="number"
                step="0.01"
                value={form.factoryPipeRun}
                onChange={(e) => updateField('factoryPipeRun', e.target.value)}
              />
            </label>
            <label>
              Difference in Length (m)
              <input type="text" value={pipeDifference} readOnly className="readonly" />
            </label>
            <label>
              Additional Charge Per Metre (kg/m)
              <input
                type="number"
                step="0.001"
                value={form.additionalPerMetre}
                onChange={(e) => updateField('additionalPerMetre', e.target.value)}
              />
            </label>
            <label>
              Factory Charge (kg)
              <input
                type="number"
                step="0.001"
                value={form.factoryCharge}
                onChange={(e) => updateField('factoryCharge', e.target.value)}
              />
            </label>
            <label>
              Total Charge (kg)
              <input type="text" value={totalCharge} readOnly className="readonly" />
            </label>
          </div>
          <p className="calc-hint">
            Auto-calculated: difference = total pipe − factory run; total charge = factory charge + (max(difference, 0) × kg/m).
            {additionalChargeKg > 0 ? ` Additional charge applied: ${additionalChargeKg} kg.` : ''}
          </p>
        </section>

        <section className="commissioning-section">
          <h2>Commissioning Checks</h2>
          <div className="form-grid">
            <label>
              1. Transit Brackets Removed
              <select value={form.check1} onChange={(e) => updateField('check1', e.target.value)}>
                {YES_NO.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </label>
            <label>
              2. Fly Leads / Elect Connections Tight
              <select value={form.check2} onChange={(e) => updateField('check2', e.target.value)}>
                {YES_NO.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </label>
            <label>
              3. Mains Fuse / MCB (Amp)
              <input value={form.check3} onChange={(e) => updateField('check3', e.target.value)} placeholder="e.g. 16" />
            </label>
            <label>
              3. Fuse / MCB Type Detail
              <input value={form.check3Detail} onChange={(e) => updateField('check3Detail', e.target.value)} placeholder="e.g. Type C" />
            </label>
            <label>
              4. Condensing Unit Nameplate FLC (Amp)
              <input value={form.check4} onChange={(e) => updateField('check4', e.target.value)} />
            </label>
            <label>
              5. AHU Unit Nameplate FLC (Amp)
              <input value={form.check5} onChange={(e) => updateField('check5', e.target.value)} />
            </label>
            <label>
              6. Pipework Suction Size
              <input value={form.suctionSize} onChange={(e) => updateField('suctionSize', e.target.value)} />
            </label>
            <label>
              6. Pipework Liquid Size
              <input value={form.liquidSize} onChange={(e) => updateField('liquidSize', e.target.value)} />
            </label>
            <label>
              7. Local Isolators Fitted
              <select value={form.check7} onChange={(e) => updateField('check7', e.target.value)}>
                {YES_NO.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </label>
            <label>
              8. Functional Test
              <select value={form.check8} onChange={(e) => updateField('check8', e.target.value)}>
                <option value="OK">OK</option>
                <option value="N/A">N/A</option>
                <option value="FAIL">FAIL</option>
              </select>
            </label>
            <label>
              9. Equipment Labelled
              <select value={form.check9} onChange={(e) => updateField('check9', e.target.value)}>
                {YES_NO.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </label>
            <label>
              10. Drains Tested
              <select value={form.check10} onChange={(e) => updateField('check10', e.target.value)}>
                {YES_NO.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </label>
            <label>
              11. Covers Fixed and Clean
              <select value={form.check11} onChange={(e) => updateField('check11', e.target.value)}>
                {YES_NO.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </label>
          </div>

          <h3>12. Condensing Unit Running Current (Amps)</h3>
          <div className="form-grid three-col">
            <label>
              Cooling L1
              <input value={form.coolL1} onChange={(e) => updateField('coolL1', e.target.value)} />
            </label>
            <label>
              Cooling L2
              <input value={form.coolL2} onChange={(e) => updateField('coolL2', e.target.value)} />
            </label>
            <label>
              Cooling L3
              <input value={form.coolL3} onChange={(e) => updateField('coolL3', e.target.value)} />
            </label>
            <label>
              Heating L1
              <input value={form.heatL1} onChange={(e) => updateField('heatL1', e.target.value)} />
            </label>
            <label>
              Heating L2
              <input value={form.heatL2} onChange={(e) => updateField('heatL2', e.target.value)} />
            </label>
            <label>
              Heating L3
              <input value={form.heatL3} onChange={(e) => updateField('heatL3', e.target.value)} />
            </label>
          </div>

          <h3>13. Air Temperatures AHU (°C)</h3>
          <div className="form-grid">
            <label>
              Cooling Air On
              <input value={form.coolAirOn} onChange={(e) => updateField('coolAirOn', e.target.value)} />
            </label>
            <label>
              Cooling Air Off
              <input value={form.coolAirOff} onChange={(e) => updateField('coolAirOff', e.target.value)} />
            </label>
            <label>
              Heating Air On
              <input value={form.heatAirOn} onChange={(e) => updateField('heatAirOn', e.target.value)} />
            </label>
            <label>
              Heating Air Off
              <input value={form.heatAirOff} onChange={(e) => updateField('heatAirOff', e.target.value)} />
            </label>
          </div>
        </section>

        <section className="commissioning-section">
          <h2>Sign-off</h2>
          <div className="form-grid">
            <label className="full-width">
              Notes
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder="Any observations or comments"
              />
            </label>
            <label>
              Witnessed By
              <input value={form.witnessedBy} onChange={(e) => updateField('witnessedBy', e.target.value)} />
            </label>
            <label>
              Witness Date
              <input type="date" value={form.witnessDate} onChange={(e) => updateField('witnessDate', e.target.value)} />
            </label>
          </div>
        </section>

        {error && <div className="form-message error">{error}</div>}
        {success && <div className="form-message success">{success}</div>}

        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={resetForm} disabled={generating}>
            Reset
          </button>
          <button type="submit" className="primary-button" disabled={generating}>
            {generating ? 'Generating…' : 'Generate Commissioning Sheet'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Commissioning;
